import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek.js';
// Idempotent — main.ts also extends it; helper modules re-extend so vitest
// (which doesn't import main.ts) still has isoWeek available.
dayjs.extend(isoWeek);
/**
 * Group slots into day columns, each holding tappable time chips, chronological
 * throughout. Slots are already time-deduped server-side (the search is scoped
 * to one therapist), so a start appears at most once. Malformed / duplicate
 * instants are dropped defensively.
 */
export function groupSlotsByDay(slots) {
    const byDay = new Map();
    const seen = new Set();
    const sorted = [...slots]
        .map((s) => (s.start instanceof Date ? s.start : new Date(s.start)))
        .filter((d) => !Number.isNaN(d.getTime()))
        .sort((a, b) => a.getTime() - b.getTime());
    for (const start of sorted) {
        const iso = start.toISOString();
        if (seen.has(iso))
            continue;
        seen.add(iso);
        const d = dayjs(start);
        const dayKey = d.format('YYYY-MM-DD');
        let column = byDay.get(dayKey);
        if (!column) {
            column = { dayKey, dayLabel: d.format('dddd, DD. MMMM YYYY'), chips: [] };
            byDay.set(dayKey, column);
        }
        column.chips.push({ startIso: iso, start, timeLabel: d.format('HH:mm') });
    }
    return [...byDay.values()];
}
/**
 * Group the suggested batch slots into calendar (ISO) weeks for the review
 * screen (PRD #1343, RS-004). Chronological throughout; malformed / duplicate
 * instants are dropped defensively. Each week is labelled with its KW number
 * and Mon–Sun span so the patient reads the batch as "a week's worth" at a
 * glance without ever seeing the frequency rule itself.
 */
export function groupSlotsByWeek(slots) {
    const byWeek = new Map();
    const seen = new Set();
    const sorted = [...slots]
        .map((s) => (s.start instanceof Date ? s.start : new Date(s.start)))
        .filter((d) => !Number.isNaN(d.getTime()))
        .sort((a, b) => a.getTime() - b.getTime());
    for (const start of sorted) {
        const iso = start.toISOString();
        if (seen.has(iso))
            continue;
        seen.add(iso);
        const d = dayjs(start);
        const weekKey = `${d.isoWeekYear()}-${String(d.isoWeek()).padStart(2, '0')}`;
        let group = byWeek.get(weekKey);
        if (!group) {
            const monday = d.startOf('isoWeek');
            const sunday = d.endOf('isoWeek');
            group = {
                weekKey,
                weekLabel: `KW ${d.isoWeek()} · ${monday.format('DD.MM.')}–${sunday.format('DD.MM.')}`,
                slots: [],
            };
            byWeek.set(weekKey, group);
        }
        group.slots.push({
            startIso: iso,
            start,
            label: `${d.format('dd, DD.MM.')} · ${d.format('HH:mm')} Uhr`,
        });
    }
    return [...byWeek.values()];
}
/**
 * The from/until window (ISO instants) scoping a same-week slot swap (PRD #1343,
 * RS-005) to the calendar (ISO) week of the slot being swapped. `from` is
 * clamped to today so the past is never searched; `until` is the end of that
 * week's Sunday. Same-week scoping is the invariant guard: swapping a slot can
 * never change how many appointments fall in the week, so the prescribed weekly
 * frequency cannot be violated by hand, and no cross-week target is ever offered.
 */
export function weekBoundsForSwap(slotStartIso, todayIso) {
    const slot = dayjs(slotStartIso);
    const monday = slot.startOf('isoWeek');
    const sunday = slot.endOf('isoWeek');
    const todayStart = dayjs(todayIso).startOf('day');
    const from = monday.isAfter(todayStart) ? monday : todayStart;
    return { from: from.toISOString(), until: sunday.toISOString() };
}
/**
 * The next `searchFrom` (YYYY-MM-DD) for "weitere Wochen laden": the day after
 * the latest slot currently shown, so the next request extends the horizon
 * without regressing. Falls back to advancing the current `searchFrom` by a week
 * when no slots were returned, so the button still makes progress on an empty
 * window.
 */
export function nextHorizonSearchFrom(columns, currentSearchFrom) {
    const lastColumn = columns[columns.length - 1];
    if (lastColumn) {
        return dayjs(lastColumn.dayKey).add(1, 'day').format('YYYY-MM-DD');
    }
    return dayjs(currentSearchFrom).add(7, 'day').format('YYYY-MM-DD');
}
//# sourceMappingURL=patientBookingSlots.js.map